import React, { useState } from 'react';
import { InputProps } from '@/types';
import Icon from '../atoms/Icon';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

type AppInputProps = InputProps & {
  showPasswordToggle?: boolean;
  /**
   * 'default' (the original behavior) renders a plain Tailwind-styled
   * <input> — used by every existing screen. 'mui' renders through real
   * MUI TextField (filled variant, theme tokens via sx) instead — for
   * screens built with the MUI-first component set (see LoginPage.tsx).
   * Same dual-mode trick AppButton.tsx/AppCard.tsx already use.
   */
  variant?: 'default' | 'mui';
};

const Input: React.FC<AppInputProps> = ({
  icon,
  error,
  label,
  helperText,
  className = '',
  type = 'text',
  showPasswordToggle = false,
  fullWidth = true,
  variant = 'default',
  ...props
}) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const inputType = showPasswordToggle ? (showPassword ? 'text' : 'password') : type;

  if (variant === 'mui') {
    const { id, ...muiRest } = props as { id?: string } & Record<string, unknown>;
    return (
      <Box className={fullWidth ? 'w-full' : ''}>
        {label && (
          <Typography
            component="label"
            htmlFor={id}
            variant="body2"
            sx={{ display: 'block', fontWeight: 600, color: 'text.primary', mb: 1 }}
          >
            {label}
          </Typography>
        )}
        <TextField
          id={id}
          type={inputType}
          fullWidth={fullWidth}
          hiddenLabel
          variant="filled"
          error={!!error}
          slotProps={{
            input: {
              disableUnderline: true,
              startAdornment: icon ? (
                <InputAdornment position="start" sx={{ pl: 2 }}>{icon}</InputAdornment>
              ) : undefined,
              endAdornment: showPasswordToggle ? (
                <InputAdornment position="end" sx={{ pr: 1 }}>
                  <IconButton onClick={() => setShowPassword((v) => !v)} tabIndex={-1} size="small">
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
              className,
              sx: {
                borderRadius: '12px', // rounded-xl
                backgroundColor: error ? '#FFF1F2' : '#EEF2FF',
                transition: 'box-shadow 0.2s ease',
                '&:hover': { backgroundColor: error ? '#FFF1F2' : '#EEF2FF' },
                '&.Mui-focused': {
                  backgroundColor: error ? '#FFF1F2' : '#EEF2FF',
                  boxShadow: `0 0 0 4px ${error ? 'rgba(225,29,72,0.1)' : 'rgba(79,70,229,0.15)'}`,
                },
              },
            },
            htmlInput: {
              sx: { py: 1.5, pl: icon ? 1 : 2, pr: 2 },
            },
          }}
          {...(muiRest as Record<string, unknown>)}
        />
        {error && (
          <Typography variant="body2" sx={{ mt: 1, color: 'error.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Icon name="AlertCircle" size={14} />
            {error}
          </Typography>
        )}
        {helperText && !error && (
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>{helperText}</Typography>
        )}
      </Box>
    );
  }

  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
          {label}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
            {icon}
          </div>
        )}
        <input
          type={inputType}
          className={`w-full px-4 ${icon ? 'pl-12' : ''} ${showPasswordToggle ? 'pr-12' : ''} py-3 rounded-xl border-2 border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all duration-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-indigo-400 ${error ? 'border-rose-500 focus:border-rose-500 focus:ring-rose-500/10' : ''} ${className}`}
          {...props}
        />
        {showPasswordToggle && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            tabIndex={-1}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        )}
      </div>
      {error && (
        <p className="mt-2 text-sm text-rose-600 dark:text-rose-400 flex items-center space-x-1">
          <Icon name="AlertCircle" size={14} />
          <span>{error}</span>
        </p>
      )}
      {helperText && !error && (
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{helperText}</p>
      )}
    </div>
  );
};

export default Input;
